import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Phone, 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  PhoneIncoming,
  PhoneOutgoing,
  DollarSign
} from 'lucide-react';
import axios from 'axios';
import { format, formatDistanceToNow } from 'date-fns';

const Calls = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCalls();
  }, [page, statusFilter, directionFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCalls = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const headers = getAuthHeaders();
      if (!headers) return;
      
      let url = `/api/calls?limit=20&offset=${page * 20}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const response = await axios.get(url, { headers });
      
      let filteredCalls = response.data;
      
      // Filter by direction if set
      if (directionFilter) {
        filteredCalls = filteredCalls.filter(call => call.direction === directionFilter);
      }
      
      // Filter by search term
      if (searchTerm) {
        filteredCalls = filteredCalls.filter(call => 
          (call.caller_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           call.caller_number?.includes(searchTerm) ||
           call.to_number?.includes(searchTerm))
        );
      }
      
      setCalls(filteredCalls);
      setHasMore(response.data.length === 20);
    } catch (error) {
      console.error('Error fetching calls:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      setError('Failed to load calls. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setError(null);
      const headers = getAuthHeaders();
      if (!headers) return;
      
      await axios.post('/api/webhooks/sync', {}, { headers });
      fetchCalls();
    } catch (error) {
      console.error('Error syncing:', error);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      setError('Failed to sync calls. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ongoing': return <span className="badge-info">Ongoing</span>;
      case 'ended': return <span className="badge-success">Completed</span>;
      case 'analyzed': return <span className="badge-success">Analyzed</span>;
      case 'error': return <span className="badge-error">Error</span>;
      default: return <span className="badge-warning">{status}</span>;
    }
  };

  const getDirectionIcon = (direction) => {
    if (direction === 'inbound') {
      return <PhoneIncoming className="w-4 h-4 text-green-600" title="Inbound" />;
    }
    return <PhoneOutgoing className="w-4 h-4 text-blue-600" title="Outbound" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button onClick={fetchCalls} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Calls</h1>
          <p className="text-gray-500">View and manage your call history</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={handleRefresh}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Sync
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field w-full pl-12"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field bg-white"
          >
            <option value="">All Status</option>
            <option value="ongoing">Ongoing</option>
            <option value="ended">Completed</option>
            <option value="analyzed">Analyzed</option>
            <option value="error">Error</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <PhoneIncoming className="w-5 h-5 text-gray-400" />
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            className="input-field bg-white"
          >
            <option value="">All Directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
          </select>
        </div>
      </div>

      {/* Calls Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Direction</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Caller</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">To</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Status</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Duration</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Cost</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Time</th>
                <th className="text-left p-4 text-gray-600 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {calls.length > 0 ? (
                calls.map((call) => (
                  <tr key={call.call_id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      {getDirectionIcon(call.direction)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {call.caller_name || 'Unknown'}
                          </p>
                          <p className="text-sm text-gray-500">{call.caller_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-gray-600">
                      {call.to_number || '-'}
                    </td>
                    <td className="p-4">{getStatusBadge(call.call_status)}</td>
                    <td className="p-4 text-gray-700">
                      {call.duration ? (
                        `${Math.floor(call.duration / 60)}m ${call.duration % 60}s`
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="p-4">
                      {call.cost !== null && call.cost !== undefined ? (
                        <span className="text-green-600 font-medium flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          {parseFloat(call.cost).toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-4 text-gray-500 text-sm">
                      {call.start_time && (
                        <>
                          <div>{format(new Date(call.start_time), 'MMM d, yyyy')}</div>
                          <div>{formatDistanceToNow(new Date(call.start_time), { addSuffix: true })}</div>
                        </>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/calls/${call.call_id}`}
                          className="btn-primary text-sm py-2 px-4"
                        >
                          View
                        </Link>
                        {call.recording_url && (
                          <a 
                            href={`/api/calls/${call.call_id}/recording`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-secondary p-2"
                            title="Download Recording"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-12 text-center">
                    <Phone className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">No calls found</h3>
                    <p className="text-gray-500">Try adjusting your filters or sync with Retell</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          
          <span className="text-gray-500">Page {page + 1}</span>
          
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Calls;